import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=recovery")) {
      setReady(true);
    } else {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
        if (event === "PASSWORD_RECOVERY") {
          setReady(true);
        }
      });
      return () => subscription.unsubscribe();
    }
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Password updated! Redirecting...");
    setTimeout(() => navigate("/app"), 1500);
  };

  return (
    <div className="min-h-screen app-bg flex items-center justify-center font-outfit">
      <div className="w-full max-w-[420px] mx-4 animate-fade-in">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-[hsl(var(--app-text-secondary))] hover:text-[hsl(var(--app-text-primary))] transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to website
        </button>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl border-2 border-[hsl(var(--app-gold))] mb-4 gold-glow">
            <Shield className="w-6 h-6 text-[hsl(var(--app-gold))]" />
          </div>
          <h1 className="font-bebas text-3xl text-[hsl(var(--app-text-primary))]">SET NEW PASSWORD</h1>
        </div>
        <div className="app-card rounded-2xl p-8">
          {ready ? (
            <form onSubmit={handleReset} className="space-y-4">
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] focus-within:border-[hsl(var(--app-gold))] transition-colors">
                <Lock className="w-4 h-4 text-[hsl(var(--app-text-muted))]" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[hsl(var(--app-text-muted))]">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button type="submit" disabled={loading} className="w-full py-4 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-bold hover:brightness-110 transition-all gold-glow disabled:opacity-50">
                {loading ? "Updating..." : "Update Password"}
              </button>
            </form>
          ) : (
            <p className="text-sm text-[hsl(var(--app-text-muted))] text-center">Invalid or expired reset link. <button onClick={() => navigate("/app")} className="text-[hsl(var(--app-gold))] hover:underline">Go to login</button></p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
